import { FC } from 'react';

import { styled } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';

import { paperClasses } from '../Paper';
import Stack, { StackProps } from '../Stack';

export interface MultiPaperProps extends StackProps {
  /**
   * If true, a visual divider will not automatically be inserted in between the neighboring Papers. The default is `false`,
   * so the divider is inserted.
   */
  noSeam?: boolean;
}

const MultiPaperStack = styled(Stack, {
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'noSeam',
})<MultiPaperProps>(({ noSeam, theme }) => ({
  [`& > .${paperClasses.root}:not(:first-of-type)`]: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderTop: 'none',
  },
  [`& > .${paperClasses.root}:not(:last-of-type), .${paperClasses.root}:not(:last-of-type) > ${paperClasses.footer}`]: {
    borderBottom: noSeam ? 'none' : `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
}));

/**
 * `MultiPaper` is a container for multiple Papers to be 'sewn together', avoiding border radii and optionally adding a seam
 * (= a visual divider between the individual Papers).
 *
 * Please only include Papers inside this component. Behavior with other children is not guaranteed.
 */
const MultiPaper: FC<MultiPaperProps> = (props) => {
  return <MultiPaperStack direction="column" {...props} />;
};

export default MultiPaper;
